"use client";

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";
import { TrendingUp, TrendingDown, Activity, RefreshCw } from "lucide-react";
import { useTransactionData } from "@/hooks/admin/use-transaction-data";
import { formatCurrency, formatCurrencyExact, transformAdminChartData, safeNumber } from "@/services/api/services/admin-dashboard";
import { formatCurrencySimple } from "@/config/utils";
import type { DailyTransactionData, TransactionStatsResponse } from "@/services/api/services/admin-dashboard";
import { Button } from "@/ui/components/primitives/button";
import { Skeleton } from "@/ui/components/primitives/skeleton";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

interface TransactionChartsProps {
  initialData?: DailyTransactionData[];
}

export function TransactionCharts({ initialData = [] }: TransactionChartsProps) {
  const {
    chartData,
    chartLoading,
    chartError,
    statsData,
    statsLoading,
    refreshAll,
    currentPeriod,
    changePeriod,
    lastUpdated
  } = useTransactionData(30000, "today", initialData);


  // The hook now handles initialData internally, so chartData is the source of truth
  // Validate that we have usable data
  const displayData = React.useMemo(() => {
    if (chartData.length > 0) {
      // Validate data structure (handle string to number conversion)
      const isValid = chartData.every(item =>
        item.date &&
        !isNaN(safeNumber(item.payinVolume)) &&
        !isNaN(safeNumber(item.payoutVolume))
      );

      if (!isValid) {
        return [];
      }

      return chartData;
    }
    return [];
  }, [chartData]);

  // Transform data for chart display
  const transformedChartData = React.useMemo(() => {
    if (displayData.length === 0) return [];

    try {
      const transformed = transformAdminChartData(displayData);
      return transformed;
    } catch (error) {
      return [];
    }
  }, [displayData]);

  // Extract today's stats from API data
  const todayStats = React.useMemo(() => {
    if (statsData && statsData.today) {
      return statsData.today;
    }
    return null;
  }, [statsData]);

  // Calculate totals from chart data as fallback
  const totalPayin = displayData.reduce((sum, item) => sum + safeNumber(item.payinVolume), 0);
  const totalPayout = displayData.reduce((sum, item) => sum + safeNumber(item.payoutVolume), 0);
  const netVolume = totalPayin - totalPayout;

  const formatVolume = (volume: number) => {
    return formatCurrency(volume);
  };

  const handleRefresh = async () => {
    await refreshAll();
  };

  // Calculate totals for display
  const total = React.useMemo(() => ({
    payin: transformedChartData.reduce((acc: number, curr) => acc + curr.payin, 0),
    payout: transformedChartData.reduce((acc: number, curr) => acc + curr.payout, 0),
  }), [transformedChartData]);

  // Fallback sample data when API fails
  const sampleChartData = React.useMemo(() => [
    { date: "Nov 17", payin: 200, payout: 0, payinCount: 1, payoutCount: 0 },
    { date: "Nov 18", payin: 3000, payout: 0, payinCount: 1, payoutCount: 0 },
    { date: "Nov 19", payin: 1000, payout: 0, payinCount: 1, payoutCount: 0 },
    { date: "Nov 20", payin: 299, payout: 0, payinCount: 1, payoutCount: 0 },
    { date: "Nov 21", payin: 299, payout: 0, payinCount: 1, payoutCount: 0 },
    { date: "Nov 22", payin: 200, payout: 950, payinCount: 1, payoutCount: 1 },
    { date: "Nov 23", payin: 200, payout: 0, payinCount: 1, payoutCount: 0 },
  ], []);

  // Use sample data if no transformed data available
  const displayChart = transformedChartData.length > 0 ? transformedChartData : sampleChartData;
  const displayTotal = transformedChartData.length > 0 ? total : {
    payin: sampleChartData.reduce((acc: number, curr) => acc + curr.payin, 0),
    payout: sampleChartData.reduce((acc: number, curr) => acc + curr.payout, 0),
  };

  
  // Show loading skeleton
  if (chartLoading && displayData.length === 0) {
    return (
      <div className="admin-grid admin-lg-2-1">
        {/* Chart skeleton */}
        <Card className="admin-card-plain">
          <CardHeader className="admin-card-header">
            <CardTitle className="admin-card-title flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Daily Transaction Volume
            </CardTitle>
          </CardHeader>
          <CardContent className="admin-card-body">
            <div className="h-64 w-full">
              <Skeleton className="h-full w-full" />
            </div>
          </CardContent>
        </Card>

        {/* Summary skeleton */}
        <Card className="admin-card-plain">
          <CardHeader className="admin-card-header">
            <CardTitle className="admin-card-title flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Volume Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="admin-card-body">
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <Skeleton className="h-3 w-20 mb-2" />
                  <Skeleton className="h-6 w-32 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-grid admin-lg-2-1">
      {/* Transaction Volume Chart */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <div className="flex items-center justify-between">
            <CardTitle className="admin-card-title flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Daily Transaction Volume
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="admin-badge-soft">
                {currentPeriod}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={statsLoading}
                className="h-8 w-8 p-0"
              >
                <RefreshCw className={`h-4 w-4 ${statsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="admin-card-body">
          {displayChart.length > 0 ? (
            <div className="space-y-6">
              {/* Header with Summary */}
              <div className="flex items-center justify-between">
                {transformedChartData.length === 0 && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs rounded">
                    Sample Data
                  </span>
                )}
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    <span className="font-medium">Payin: {formatVolume(displayTotal.payin)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                    <span className="font-medium">Payout: {formatVolume(displayTotal.payout)}</span>
                  </div>
                </div>
              </div>

              {/* AreaChart */}
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={displayChart}>
                  <defs>
                    <linearGradient id="payinGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="payoutGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="payin"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#payinGradient)"
                    name="Payin"
                  />
                  <Area
                    type="monotone"
                    dataKey="payout"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#payoutGradient)"
                    name="Payout"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : chartError ? (
            <div className="h-64 w-full flex items-center justify-center">
              <div className="text-center">
                <Activity className="h-8 w-8 mx-auto mb-2 text-destructive" />
                <p className="admin-text-sm text-destructive mb-1">Error loading chart data</p>
                <p className="admin-text-xs admin-text-muted mb-2">{chartError}</p>
                <Button variant="outline" size="sm" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <div className="h-64 w-full flex items-center justify-center">
              <div className="text-center">
                <Activity className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="admin-text-sm admin-text-muted mb-2">No transaction data available</p>
                <p className="admin-text-xs admin-text-muted mb-3">Check console for debugging information</p>
                <Button variant="outline" size="sm" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Volume Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          {todayStats ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-3 w-3" style={{ color: 'var(--chart-2)' }} />
                  <span className="admin-text-xs admin-text-muted">Total Payin</span>
                </div>
                <div className="admin-text-lg" style={{ fontWeight: 600, color: 'var(--chart-2)' }}>
                  {formatCurrencySimple(todayStats.totalPayinSuccess)}
                </div>
                <div className="admin-text-xs admin-text-muted" style={{ marginTop: '2px' }}>
                  {todayStats.totalCount} transactions
                </div>
              </div>

              <div style={{ height: '1px', background: 'var(--border)' }} />

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-3 w-3" style={{ color: 'var(--chart-4)' }} />
                  <span className="admin-text-xs admin-text-muted">Total Payout</span>
                </div>
                <div className="admin-text-lg" style={{ fontWeight: 600, color: 'var(--chart-4)' }}>
                  {formatCurrencySimple(todayStats.totalPayoutSuccess)}
                </div>
                <div className="admin-text-xs admin-text-muted" style={{ marginTop: '2px' }}>
                  {todayStats.totalPayoutSuccess > 0 ? 'Payout transactions' : 'No payouts'}
                </div>
              </div>

              <div style={{ height: '1px', background: 'var(--border)' }} />

              <div>
                <div className="admin-text-xs admin-text-muted mb-2">Net Volume</div>
                <div className="admin-text-lg" style={{
                  fontWeight: 600,
                  color: todayStats.totalPayinSuccess >= todayStats.totalPayoutSuccess ? 'var(--chart-2)' : 'var(--destructive)'
                }}>
                  {formatCurrencySimple(todayStats.totalPayinSuccess - todayStats.totalPayoutSuccess)}
                </div>
                <div className="admin-text-xs admin-text-muted" style={{ marginTop: '2px' }}>
                  {netVolume >= 0 ? 'Positive' : 'Negative'} cash flow
                </div>
              </div>

              <div style={{ height: '1px', background: 'var(--border)' }} />

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="admin-text-muted">Total Volume</div>
                  <div className="font-semibold">{formatCurrencySimple(todayStats.totalVolume)}</div>
                </div>
                <div>
                  <div className="admin-text-muted">Total Fee</div>
                  <div className="font-semibold">{formatCurrencySimple(todayStats.totalFee)}</div>
                </div>
                <div>
                  <div className="admin-text-muted">Average Amount</div>
                  <div className="font-semibold">{formatCurrencySimple(todayStats.averageAmount)}</div>
                </div>
                <div>
                  <div className="admin-text-muted">Success Rate</div>
                  <div className="font-semibold">{todayStats.successRate}%</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="admin-text-sm admin-text-muted">No volume data available</p>
              </div>
            </div>
          )}

          {lastUpdated && (
            <div className="admin-text-xs admin-text-muted" style={{ marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}